// src/pages/student/StudentDashboard.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { Card, Button, Table, Badge, Spinner, Row, Col, Dropdown } from "react-bootstrap";
import { collection, doc, getDoc, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useNavigate } from "react-router-dom";
import { useBrand, hexToRgba } from "./_brand";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";

import "./studentDashboard.soft.css";

/* 3D PNGs (adjust paths if needed) */
import PNG_Profile     from "../../assets/3d/profile.png";
import PNG_Classes     from "../../assets/3d/online-class.png";
import PNG_Assignments from "../../assets/3d/assignments.png";
import PNG_Progress    from "../../assets/3d/progress.png";
import PNG_Exams       from "../../assets/3d/exams.png";
import PNG_Fees        from "../../assets/3d/fees.png";
import PNG_Attendance  from "../../assets/3d/attendance.png";
import PNG_Circulars   from "../../assets/3d/megaphone.png";

/* Hero illustration for the welcome banner (right) */
import PNG_Hero        from "../../assets/3d/hero-student.png";

/* ---------------- helpers ---------------- */
const fmt = (ts, f = "DD MMM YYYY, h:mm A") =>
  ts?.toDate ? dayjs(ts.toDate()).format(f) : ts ? dayjs(ts).format(f) : "—";
const safe = (v, fb = "—") => (v ? v : fb);
const fmtDOB = (d) => {
  const js = d?.toDate ? d.toDate() : d ? new Date(d) : null;
  return js && !isNaN(+js) ? dayjs(js).format("DD MMM YYYY") : "—";
};

/* light tilt + ripple */
const tiltMove = (e) => {
  const el = e.currentTarget; const r = el.getBoundingClientRect();
  const px = (e.clientX - r.left) / r.width; const py = (e.clientY - r.top) / r.height;
  el.style.setProperty("--mx", `${px * 100}%`);
  el.style.setProperty("--my", `${py * 100}%`);
  el.style.setProperty("--rx", `${(py - 0.5) * 7}deg`);
  el.style.setProperty("--ry", `${(px - 0.5) * -7}deg`);
};
const tiltLeave = (e) => {
  const el = e.currentTarget;
  el.style.setProperty("--mx", "50%");
  el.style.setProperty("--my", "50%");
  el.style.setProperty("--rx", "0deg");
  el.style.setProperty("--ry", "0deg");
  el.classList.remove("ripple-go");
};
const ripple = (e) => {
  const el = e.currentTarget; const r = el.getBoundingClientRect();
  el.style.setProperty("--rpx", `${e.clientX - r.left}px`);
  el.style.setProperty("--rpy", `${e.clientY - r.top}px`);
  el.classList.remove("ripple-go"); void el.offsetWidth; el.classList.add("ripple-go");
};

/* palette + gradient helpers */
const normalizeHex6 = (hex) => {
  let v = String(hex || "").trim().replace(/^#/, "");
  if (v.length === 3) v = v.split("").map((x) => x + x).join("");
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : "#3b82f6";
};
const clamp = (n, min=0, max=1) => Math.min(max, Math.max(min, n));
const hexToRgb = (h) => {
  let v = String(h || "").trim().replace("#", "");
  if (v.length === 3) v = v.split("").map((x) => x + x).join("");
  const n = parseInt(v, 16);
  if (!Number.isFinite(n)) return { r: 59, g: 130, b: 246 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const rgbToHex = ({ r, g, b }) => "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
const rgbToHsl = ({ r, g, b }) => {
  r/=255; g/=255; b/=255; const max=Math.max(r,g,b), min=Math.min(r,g,b);
  let h, s, l=(max+min)/2; if(max===min){h=s=0;}else{const d=max-min; s=l>.5? d/(2-max-min): d/(max+min);
  switch(max){case r:h=(g-b)/d+(g<b?6:0);break;case g:h=(b-r)/d+2;break;default:h=(r-g)/d+4;} h*=60;}
  return {h,s,l};
};
const hslToRgb = ({ h, s, l }) => {
  h=((h%360)+360)%360; const c=(1-Math.abs(2*l-1))*s; const x=c*(1-Math.abs(((h/60)%2)-1)); const m=l-c/2;
  let rp=0,gp=0,bp=0;
  if (0<=h && h<60){ rp=c; gp=x; bp=0; }
  else if (60<=h && h<120){ rp=x; gp=c; bp=0; }
  else if (120<=h && h<180){ rp=0; gp=c; bp=x; }
  else if (180<=h && h<240){ rp=0; gp=x; bp=c; }
  else if (240<=h && h<300){ rp=x; gp=0; bp=c; }
  else { rp=c; gp=0; bp=x; }
  return { r: Math.round((rp+m)*255), g: Math.round((gp+m)*255), b: Math.round((bp+m)*255) };
};
const hslToHex = (h,s,l) => rgbToHex(hslToRgb({h,s,l}));
const rotateHue = (h, deg) => (h + deg + 360) % 360;

const gradientFromAccent = (accentHex, mode="auto") => {
  const { h, s, l } = rgbToHsl(hexToRgb(accentHex)); let use = mode;
  if (mode==="auto") use = s < .18 ? "mono" : s > .6 ? "split" : "analog";
  let g1,g2;
  if (use==="mono"){ g1=hslToHex(h, Math.min(.18, s+.06), Math.max(.14, l*.55));
                     g2=hslToHex(h, Math.min(.22, s+.10), Math.max(.22, l*.75)); }
  else if (use==="split"){ g1=hslToHex(rotateHue(h,-30), clamp(s*.9,.35,.9),  clamp(l*.85,.22,.65));
                           g2=hslToHex(rotateHue(h, 30), clamp(s*.95,.4,.95),  clamp(l*1.05,.3,.7)); }
  else { g1=hslToHex(rotateHue(h,-12), clamp(s*.95,.28,.95), clamp(l*.92,.22,.70));
         g2=hslToHex(rotateHue(h,  8), clamp(s*1.05,.30,.98), clamp(l*1.04,.28,.76)); }
  return { g1, g2, angle: 125, stripe: hexToRgba(g2, .08), ring: hexToRgba(accentHex, .35) };
};

/* Mini theme picker */
function AccentDropdown({ active, onPick, align="end", size="sm" }) {
  const colors = ["#3b82f6","#0ea5e9","#22c55e","#f59e0b","#ef4444","#8b5cf6","#14b8a6","#111827","#e2e8f0","#ffffff"];
  return (
    <Dropdown align={align}>
      <Dropdown.Toggle size={size} variant="outline-secondary" className="theme-toggle d-inline-flex align-items-center gap-2">
        <span className="theme-chip" style={{ background: active }} /> Theme
      </Dropdown.Toggle>
      <Dropdown.Menu className="theme-picker shadow">
        <div className="swatch-grid">
          {colors.map((c)=>(<button key={c} className="swatch-btn" style={{background:c, border: ["#ffffff","#e2e8f0"].includes(c)?"1px solid rgba(0,0,0,.25)":"none"}}
            onClick={()=>onPick?.(c)} aria-label={`Pick ${c}`} title={c}/>))}
          <label className="swatch-btn custom" title="Custom color">
            <span className="plus">＋</span>
            <input className="visually-hidden" type="color" onChange={(e)=>onPick?.(e.target.value)}/>
          </label>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

/* ───────────────────────────────────────────── */

export default function StudentDashboard() {
  const navigate = useNavigate();
  const brand = useBrand();
  const { primary, accent, setPrimary, setAccent } = brand || {};

  const primaryHex  = useMemo(()=>normalizeHex6(primary || "#3b82f6"), [primary]);
  const accentHex   = useMemo(()=>normalizeHex6(accent  || primaryHex), [accent, primaryHex]);
  const g = useMemo(()=>gradientFromAccent(accentHex), [accentHex]);

  useEffect(()=>{ const root = document.documentElement;
    root.style.setProperty("--brand-primary", primaryHex);
    root.style.setProperty("--brand-accent",  accentHex);
  }, [primaryHex, accentHex]);

  const onPickTheme = useCallback((c)=>{ const col = normalizeHex6(c); setPrimary?.(col); setAccent?.(col); }, [setPrimary, setAccent]);

  // ids
  const [schoolId]    = useState(()=>localStorage.getItem("schoolId"));
  const [studentId]   = useState(()=>localStorage.getItem("studentId"));
  const [studentName] = useState(()=>localStorage.getItem("studentName") || "");

  // states
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [ledger, setLedger] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(true);
  const [schoolName, setSchoolName] = useState("");

  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

  useEffect(()=>{ if(!schoolId || !studentId){ toast.error("Missing school/student info. Please sign in again."); navigate("/login?role=student",{replace:true}); }}, [schoolId, studentId, navigate]);

  // profile
  useEffect(() => {
    if (!schoolId || !studentId) return;
    setLoadingProfile(true);
    const ref = doc(db, "schools", schoolId, "students", studentId);
    const unsub = onSnapshot(ref, (snap)=>{
      setProfile(snap.exists()? { id: snap.id, ...snap.data() } : null);
      setLoadingProfile(false);
    }, (err)=>{
      console.error(err); setLoadingProfile(false);
      toast.error(err?.code==="permission-denied" ? "No permission to view profile." : "Failed to load profile.");
      if (err?.code==="permission-denied") navigate("/login?role=student",{replace:true});
    });
    return () => unsub();
  }, [schoolId, studentId, navigate]);

  // ledger + totals
  useEffect(() => {
    if (!schoolId || !studentId) return;
    setLoadingLedger(true);
    const paysCol = collection(db, "schools", schoolId, "students", studentId, "payments");
    const unsub1 = onSnapshot(query(paysCol, orderBy("createdAt","desc")), (snap)=>{
      setLedger(snap.docs.map((d)=>({id:d.id,...d.data()}))); setLoadingLedger(false);
    }, ()=> setLoadingLedger(false));
    const totalsRef = doc(db, "schools", schoolId, "fees_studentTotals", studentId);
    const unsub2 = onSnapshot(totalsRef, (snap)=> setTotals(snap.exists()? snap.data(): null));
    return ()=>{ unsub1(); unsub2(); };
  }, [schoolId, studentId]);

  // school name
  useEffect(()=>{ (async ()=>{ if(!schoolId) return;
    try{ const s=await getDoc(doc(db,"schools",schoolId)); if(s.exists()) setSchoolName(s.data()?.name || ""); }catch(e){console.error(e);}
  })(); }, [schoolId]);

  const classTag = useMemo(()=>[profile?.className, profile?.section].filter(Boolean).join("-") || "—",[profile?.className, profile?.section]);
  const paidTotal = useMemo(()=> {
    const serverPaid = Number(totals?.paidTotal); if(Number.isFinite(serverPaid)) return serverPaid;
    return ledger.reduce((s,p)=> s + (Number.isFinite(Number(p.amount))? Number(p.amount):0), 0);
  }, [totals?.paidTotal, ledger]);

  const goto = (path) => navigate(path);

  return (
    <div className="dashboard-wrapper">
      {/* BACKDROP */}
      <div className="position-fixed top-0 start-0 w-100 h-100" style={{
        zIndex: 0,
        background: `
          radial-gradient(1100px 520px at 12% 8%, ${hexToRgba(g.g1,.12)} 0%, transparent 60%),
          radial-gradient(1000px 540px at 90% 88%, ${hexToRgba(g.g2,.12)} 0%, transparent 60%),
          linear-gradient(180deg, var(--bs-body-bg) 0%, var(--bs-body-bg) 100%)
        `
      }}/>

      <Particles
        id="student-bg"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        options={{
          fpsLimit: 60, fullScreen: { enable:false }, background: { color:{ value:"transparent"} },
          detectRetina: true,
          interactivity: { events: { onHover:{ enable:true, mode:"repulse"}, onClick:{ enable:true, mode:"push"}, resize:true },
            modes: { repulse:{ distance:120, duration:.35 }, push:{ quantity:2 } } },
          particles: { number: { value: 52, density: { enable: true, area: 900 } },
            color: { value: [g.g1, g.g2] },
            links: { enable: true, color: hexToRgba(primaryHex,.45), distance: 140, opacity: .45, width: 1 },
            move: { enable: true, speed: 1.05, outModes:{ default:"out" } },
            opacity: { value: .52 }, size: { value: { min:1, max:3 } } },
          responsive: [
            { maxWidth: 768, options: { particles: { number: { value: 34 } } } },
            { maxWidth: 420, options: { particles: { number: { value: 20 } } } }
          ]
        }}
        style={{ zIndex: 1, pointerEvents: "none" }}
      />

      {/* FOREGROUND */}
      <div className="page-shell">
        {/* HERO */}
        <section className="hero-plate rounded-5 mb-3" style={{ "--h1": g.g1, "--h2": g.g2 }}>
          <div className="hero-plate__bg" aria-hidden />
          <div className="hero-plate__inner">
            <div className="hero-plate__left">
              <div className="hero-plate__date">{dayjs().format("MMMM D,  YYYY")}</div>
              <h1 className="hero-plate__title">
                Welcome back, <span className="hero-plate__accent">{studentName || "Student"}</span>!
                <span className="wave">👋</span>
              </h1>
               <small className="hero-plate__sub">
               {schoolName}
            </small>
              <p className="hero-plate__sub">Always stay updated in your student portal</p>
            </div>
            <div className="hero-plate__right">
              <img src={PNG_Hero} alt="" className="hero-plate__img" loading="eager" />
              <span className="hero-float hero-float--cap" />
              <span className="hero-float hero-float--ball" />
              <span className="hero-float hero-float--badge" />
              <span className="hero-float hero-float--spring" />
            </div>
          </div>
        </section>

        {/* PROFILE + FEES */}
        <Row className="g-3">
          {/* PROFILE */}
          <Col xl={6}>
            <Card className="neo-soft" style={{ "--c1": g.g1, "--c2": g.g2 }}>
              <Card.Header className="neo-soft__hdr">
                <div className="d-flex align-items-center gap-2">
                  <span className="hdr-dot" aria-hidden />
                  <span className="fw-semibold">Profile</span>
                </div>
                <Button size="sm" className="pill-btn" onClick={() => goto("/student/profile")}>Open</Button>
              </Card.Header>

              <Card.Body className="neo-soft__body">
                {loadingProfile ? (
                  <div className="skeleton">
                    <div className="sk-avatar" />
                    <div className="sk-lines"><span /><span /><span /><span /></div>
                  </div>
                ) : !profile ? (
                  <div className="text-muted">No profile found.</div>
                ) : (
                  <div className="row g-3 align-items-center">
                    <div className="col-auto">
                      <div className="photo-shell">
                        {profile.photoURL ? <img src={profile.photoURL} alt={profile.name} /> : <div className="ph-placeholder"><i className="bi bi-person" /></div>}
                      </div>
                    </div>
                    <div className="col">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-semibold">{safe(profile.name)}</span>
                        {profile.status === "inactive" ? <span className="chip chip--muted">Inactive</span> : <span className="chip chip--ok">Active</span>}
                      </div>

                      <div className="meta-grid">
                        <div><span>Class</span><strong>{classTag}</strong></div>
                        <div><span>Batch</span><strong>{safe(profile.branch)}</strong></div>
                        <div><span>Roll</span><strong>{safe(profile.rollNo)}</strong></div>
                      </div>

                      <div className="small text-muted mt-2">
                        Email:{" "}
                        <a href={`mailto:${safe(profile.email)}`} className="fw-semibold link-underline-opacity-0" style={{ color: g.g1 }}>
                          {safe(profile.email)}
                        </a>
                        <span className="mx-2">•</span>
                        DOB: {fmtDOB(profile.dob)}
                      </div>
                    </div>
                  </div>
                )}
              </Card.Body>

              <span className="neo-orb neo-orb--tl" aria-hidden />
              <span className="neo-orb neo-orb--br" aria-hidden />
            </Card>
          </Col>

          {/* FEES */}
          <Col xl={6}>
            <Card className="neo-soft" style={{ "--c1": g.g1, "--c2": g.g2 }}>
              <Card.Header className="neo-soft__hdr">
                <div className="d-flex align-items-center gap-2">
                  <span className="hdr-dot hdr-dot--alt" aria-hidden />
                  <span className="fw-semibold">Fees Summary</span>
                </div>
                <Button size="sm" className="pill-btn" onClick={() => goto("/student/fees")}>View details</Button>
              </Card.Header>

              <Card.Body className="neo-soft__body">
                {loadingLedger ? (
                  <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading summary…</div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="text-muted">Total Paid</div>
                      <div className="fs-5 fw-bold">₹{paidTotal.toLocaleString()}</div>
                    </div>

                    <div className="meter">
                      <div
                        className="meter__bar"
                        style={{
                          width: `${totals?.feeTotal ? Math.min(100, (paidTotal / totals.feeTotal) * 100) : 100}%`,
                        }}
                      >
                        <span className="meter__tag">₹{paidTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-3 mt-2 small text-muted">
                      <div>
                        Last payment:{" "}
                        {totals?.lastPaymentAt ? fmt(totals.lastPaymentAt) : ledger[0] ? fmt(ledger[0].createdAt) : "—"}
                      </div>
                      {Number.isFinite(Number(totals?.feeTotal)) && (
                        <div className="chip chip--ghost">Total Fee: ₹{Number(totals.feeTotal).toLocaleString()}</div>
                      )}
                    </div>
                  </>
                )}
              </Card.Body>

              <span className="neo-orb neo-orb--tl" aria-hidden />
              <span className="neo-orb neo-orb--br" aria-hidden />
            </Card>
          </Col>
        </Row>

        {/* FEATURE TILES */}
        <Row className="g-3 mt-1">
          {[
            { to:"/student/profile",     title:"Profile",        sub:"View & update school profile",  img:PNG_Profile },
            { to:"/student/classes",     title:"Online Classes", sub:"Join live sessions & schedule", img:PNG_Classes },
            { to:"/student/assignments", title:"Assignments",    sub:"Submit work & deadlines",       img:PNG_Assignments },
            { to:"/student/progress",    title:"Progress Card",  sub:"Marks, grades, performance",    img:PNG_Progress },
            { to:"/student/exams",       title:"Examinations",   sub:"Schedules, halls & results",    img:PNG_Exams },
            { to:"/student/fees",        title:"Fees",           sub:"See your payment details",      img:PNG_Fees },
            { to:"/student/attendance",  title:"Attendance",     sub:"Daily / monthly overview",      img:PNG_Attendance },
            { to:"/student/notices",     title:"Circulars",      sub:"Announcements & notices",       img:PNG_Circulars },
          ].map((t,i)=>(
            <Col key={t.to} xs={12} md={6} lg={3}>
              <Card
                role="button"
                aria-label={`${t.title}: ${t.sub}`}
                onClick={()=>goto(t.to)}
                onMouseMove={tiltMove}
                onMouseLeave={tiltLeave}
                onMouseDown={ripple}
                className="modern-card"
                style={{ "--g1": g.g1, "--g2": g.g2, "--angle": `${(g.angle + i*7)%360}deg` }}
              >
                <span className="mc-aurora"/><span className="mc-glare"/><span className="mc-sheen"/><span className="mc-ripple"/>
                <Card.Body className="d-flex align-items-center gap-3">
                  <div className="modern-card-ico"><img src={t.img} alt="" loading="lazy"/></div>
                  <div className="modern-card-body">
                    <div className="modern-card-title">{t.title}</div>
                    <div className="modern-card-sub">{t.sub}</div>
                  </div>
                  <button className="modern-btn" type="button">Open</button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* RECENT PAYMENTS — SINGLE SCROLL (no inner scrolling) */}
        <Card className="border-0 shadow-sm mt-3 glass-card rounded-4 overflow-hidden">
          <Card.Header className="card-header--glow">Recent Payments</Card.Header>
          <div className="table-responsive no-max-height">
            <Table hover className="mb-0 align-middle">
              <thead className="table-light sticky-top">
                <tr><th>Date</th><th>Amount</th><th>Category</th><th>Month</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {loadingLedger ? (
                  <tr><td colSpan={5} className="text-center text-muted py-4"><Spinner animation="border" size="sm"/> Loading…</td></tr>
                ) : ledger.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-muted py-4">No payments yet.</td></tr>
                ) : (
                  ledger.slice(0,10).map((p)=>(
                    <tr key={p.id}>
                      <td>{fmt(p.createdAt)}</td>
                      <td>₹{(Number.isFinite(Number(p.amount)) ? Number(p.amount) : 0).toLocaleString()}</td>
                      <td>{p.category || "—"}</td>
                      <td>{p.month || "—"}</td>
                      <td className="text-truncate" style={{ maxWidth: 260 }} title={p.notes || ""}>{p.notes || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>

       

      </div>
    </div>
  );
}
